/**
 * Turning a conversational turn into a retrieval query.
 *
 * Two failures from Free's transcript motivate this, both visible in one
 * exchange. He asked about CSS containers, got an answer, then asked "please
 * search your memories for more info" and received a byte-identical reply.
 *
 * FAILURE ONE: the turn was embedded literally. "please search your memories
 * for more info" has no topic in it, so it retrieved against the words
 * "search", "memories" and "info" rather than against CSS containers. Whatever
 * came back could not possibly have been new.
 *
 * FAILURE TWO: the tool had been withheld. Suppressing it when prefetch
 * succeeds is right for the common case, but it made an explicit request to go
 * looking literally impossible to obey.
 *
 * Both are handled here: detect a turn that is ABOUT searching rather than
 * about a subject, carry the topic forward from the conversation, and tell the
 * caller to put the tool back within reach.
 */

/**
 * Turns whose content is a request to look harder, not a subject in itself.
 * Deliberately narrow: a false positive drags stale topic words into a genuine
 * new question, which is worse than missing one of these.
 */
const META_REQUEST =
  /\b(search|look|dig|check|query)\b.{0,24}\b(again|memor|lattice|record|deeper|harder|more)\b|\b(tell me|know|got|anything|what's|whats)\b.{0,16}\b(more|else)\b|\bgo deeper\b|\bmore (info|detail|specifics)\b|\bkeep going\b|\belaborate\b/i;

/** A turn this short almost never carries enough to retrieve against on its own. */
const SHORT_TURN_CHARS = 28;

export type ShapedQuery = {
  /** What to embed. */
  query: string;
  /** True when the topic had to be carried in from earlier turns. */
  carriedContext: boolean;
  /** True when the user is explicitly asking to go looking again. */
  explicitSearch: boolean;
};

export function isMetaRequest(turn: string): boolean {
  return META_REQUEST.test(turn.trim());
}

/**
 * Build the retrieval query for a turn.
 *
 * `priorUserTurns` is oldest-first. Only user turns are used: an assistant
 * reply is mostly this lattice's own words already, so folding it in biases
 * retrieval toward what was just said rather than toward what was asked.
 */
export function shapeQuery(turn: string, priorUserTurns: string[] = []): ShapedQuery {
  const t = turn.trim();
  const explicitSearch = isMetaRequest(t);
  const needsContext = explicitSearch || t.length < SHORT_TURN_CHARS;

  if (!needsContext || priorUserTurns.length === 0) {
    return { query: t, carriedContext: false, explicitSearch };
  }

  // Walk back for the most recent turn that carried an actual subject, so a
  // run of "more?" / "go on" does not lose the thread entirely.
  const topic = [...priorUserTurns].reverse().find((p) => {
    const s = p.trim();
    return s.length >= SHORT_TURN_CHARS && !isMetaRequest(s);
  });

  if (!topic) return { query: t, carriedContext: false, explicitSearch };

  // Topic first: it is what should dominate the embedding. The current turn
  // still contributes, since "more about the syntax" should pull differently
  // from "more about the browser support".
  return { query: `${topic.trim()} ${t}`, carriedContext: true, explicitSearch };
}

/**
 * Facts already shown this turn, so a repeat search can be told to surface
 * something else rather than handing back the same rows.
 */
export function freshOnly<T extends { index: number }>(hits: T[], alreadySeen: Set<number>): T[] {
  return hits.filter((h) => !alreadySeen.has(h.index));
}
